<template>
  <div>
    <h5>Carrier Report</h5>

    <b-card class="mb-4 report-filters-card">
      <b-form inline class="report-filters" @submit.prevent="fetchReport">
        <ReportPeriodFilter
          :period-type.sync="periodType"
          :time-period.sync="timePeriod"
          :selected-dates.sync="selectedDates"
          :time-options="timeOptions"
          :date-config="dateConfig"
          radio-name="carrierReportPeriodType"
        />
        <button class="btn btn-primary">Show Report</button>
      </b-form>
      <small class="text-muted d-block mt-3">
        Dispatch metrics include every historical carrier assignment, including archived and later undispatched orders. The period is limited to one year.
      </small>
    </b-card>

    <LoadingIndicator v-if="loading" centered />
    <span v-else-if="empty">No records found</span>

    <b-card v-else-if="items.length" class="mb-4 report-results" no-body>
      <b-table responsive striped hover small :items="items" :fields="fields" class="mb-0" show-empty>
        <template #table-caption>Carrier activity from {{ periodLabel }}</template>
        <template #cell(carrierId)="data">
          <b-link :to="carrierLink(data.value)" target="_blank">{{ carrierName(data.item) }}</b-link>
        </template>
        <template #cell(totalDispatchedCount)="data">
          <MetricStack
            :items="[
              { label: 'Total dispatched', value: data.item.totalDispatchedCount },
              { label: 'Currently assigned', value: data.item.currentlyDispatchedCount },
              { label: 'No longer assigned', value: formatCountRatio(data.item.undispatchedCount, data.item.undispatchedRatio) },
            ]"
          />
        </template>
        <template #cell(cdRequestsCount)="data">
          <MetricStack
            :items="[
              { label: 'Requests', value: data.item.cdRequestsCount },
              { label: 'Converted', value: data.item.cdConvertedRequestsCount },
              { label: 'Success rate', value: formatPercentage(data.item.cdConversionRatio) },
              { label: 'Above posted rate', value: data.item.cdAbovePostedRateCount },
            ]"
          />
        </template>
        <template #cell(carrierMistakesCount)="data">
          <MetricStack
            :items="[
              { label: 'Carrier mistakes', value: formatCountRatio(data.item.carrierMistakesCount, data.item.carrierMistakeRatio) },
              { label: 'Completed', value: data.item.completedCount },
              { label: 'Approvals', value: formatCountRatio(data.item.approvalsCount, data.item.approvalsRatio) },
            ]"
          />
        </template>
        <template #cell(dispatchedBeforePuw)="data">
          <MetricStack
            :items="[
              { label: 'Before window', value: data.item.dispatchedBeforePuw },
              { label: 'During window', value: data.item.dispatchedDuringPuw },
              { label: 'After window', value: data.item.dispatchedAfterPuw },
            ]"
          />
        </template>
        <template #cell(showDetails)="data">
          <b-button size="sm" variant="outline-primary" @click.prevent="showDetails(data.item)">View orders</b-button>
        </template>
        <template #custom-foot>
          <b-tr class="font-weight-bold">
            <b-th>Totals</b-th>
            <b-th><MetricStack :items="dispatchMetrics(totals)" /></b-th>
            <b-th>{{ formatCurrency(totals.averageBrokerFee) }}</b-th>
            <b-th><MetricStack :items="cdMetrics(totals)" /></b-th>
            <b-th><MetricStack :items="qualityMetrics(totals)" /></b-th>
            <b-th><MetricStack :items="pickupMetrics(totals)" /></b-th>
            <b-th />
          </b-tr>
        </template>
      </b-table>
    </b-card>

    <b-modal id="carrier-report-details" :title="detailsTitle" size="xl" scrollable hide-footer>
      <LoadingIndicator v-if="activeCarrier && activeCarrier.loading" centered />
      <b-table v-else responsive striped hover small :items="activeCarrier ? activeCarrier.rows : []" :fields="detailsFields" class="mb-0 details-table" show-empty>
        <template #cell(id)="data">
          <b-link :to="orderLink(data.value)" target="_blank">{{ data.value }}</b-link>
        </template>
        <template #cell(dispatchedAt)="data">{{ data.value | formatDateTime }}</template>
        <template #cell(isCurrentlyDispatched)="data">{{ data.value ? 'Yes' : 'No' }}</template>
        <template #cell(pickupWindow)="data">
          <span>{{ data.item.pickupWindowTiming }}</span>
          <small class="text-muted d-block">{{ pickupWindowRange(data.item) }}</small>
        </template>
        <template #cell(deletedAt)="data">{{ data.value ? 'Yes' : 'No' }}</template>
      </b-table>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment';
import ReportPeriodFilter from '@/components/pages/reports/ReportPeriodFilter';
import LoadingIndicator from '@/components/shared/LoadingIndicator';
import { ApiClient } from '@/api/ApiClient';

const MetricStack = {
  functional: true,
  props: { items: { type: Array, required: true } },
  render(h, { props }) {
    return h(
      'div',
      { class: 'metric-stack' },
      props.items.map(item =>
        h('div', { class: 'metric-stack__item' }, [
          h('span', { class: 'metric-stack__label' }, item.label),
          h('strong', String(item.value === null || item.value === undefined ? '-' : item.value)),
        ])
      )
    );
  },
};

export default {
  name: 'ReportCarriers',
  components: { LoadingIndicator, MetricStack, ReportPeriodFilter },
  data() {
    const currency = value => (value === null || value === undefined ? '-' : `$${Number(value).toLocaleString()}`);
    return {
      loading: false,
      empty: false,
      items: [],
      totals: {},
      activeCarrier: null,
      periodType: 'list',
      timePeriod: 1,
      selectedDates: null,
      form: { periodStart: null, periodEnd: null },
      dateConfig: { mode: 'range', dateFormat: 'Y-m-d', altInput: true, altFormat: 'm/d/Y', allowInput: true },
      timeOptions: [
        { text: 'Current month', value: 1 },
        { text: 'Last month', value: 2 },
        { text: 'Current quarter', value: 3 },
        { text: 'Last quarter', value: 4 },
        { text: 'Current year', value: 5 },
      ],
      fields: [
        { key: 'carrierId', label: 'Carrier' },
        { key: 'totalDispatchedCount', label: 'Dispatch Assignments', sortable: true },
        { key: 'averageBrokerFee', label: 'Avg Broker Fee', sortable: true, formatter: currency },
        { key: 'cdRequestsCount', label: 'CD Activity', sortable: true },
        { key: 'carrierMistakesCount', label: 'Quality', sortable: true },
        { key: 'dispatchedBeforePuw', label: 'Pickup Timing', sortable: true },
        { key: 'showDetails', label: '' },
      ],
      detailsFields: [
        { key: 'id', label: 'Order', sortable: true },
        { key: 'dispatchedAt', label: 'Dispatched At', sortable: true },
        { key: 'isCurrentlyDispatched', label: 'Current', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'pickupWindow', label: 'Pickup Window' },
        { key: 'brokerFee', label: 'Broker Fee', sortable: true, formatter: currency },
        { key: 'deletedAt', label: 'Archived', sortable: true },
      ],
    };
  },
  computed: {
    detailsTitle() {
      return this.activeCarrier ? `${this.carrierName(this.activeCarrier)} orders` : 'Carrier orders';
    },
    periodLabel() {
      if (!this.form.periodStart) return 'all time';
      return `${this.form.periodStart} through ${this.form.periodEnd}`;
    },
  },
  methods: {
    prepareDates() {
      let dates = [];
      if (this.periodType === 'custom') {
        if (!this.selectedDates) return false;
        dates = this.selectedDates.split(' to ');
        if (dates.length < 2) dates.push(dates[0]);
      } else if (this.timePeriod === 1) dates = [moment().startOf('month'), moment().endOf('month')];
      else if (this.timePeriod === 2) dates = [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')];
      else if (this.timePeriod === 3) dates = [moment().startOf('quarter'), moment().endOf('quarter')];
      else if (this.timePeriod === 4) dates = [moment().subtract(1, 'quarter').startOf('quarter'), moment().subtract(1, 'quarter').endOf('quarter')];
      else if (this.timePeriod === 5) dates = [moment().startOf('year'), moment().endOf('year')];

      this.form.periodStart = dates[0] ? moment(dates[0]).format('YYYY-MM-DD') : null;
      this.form.periodEnd = dates[1] ? moment(dates[1]).format('YYYY-MM-DD') : null;
      if (moment(this.form.periodEnd).isAfter(moment(this.form.periodStart).add(1, 'year'))) {
        this.$bvToast.toast('The report period cannot be longer than one year.', {
          title: 'Invalid period',
          variant: 'danger',
          solid: true,
        });
        return false;
      }
      return true;
    },
    async fetchReport() {
      if (!this.prepareDates()) return;
      this.loading = true;
      try {
        const response = await ApiClient.reports.carriers({ params: this.form });
        this.items = response && response.data && response.data.data ? response.data.data.rows.map(item => ({ ...item, rows: [], loading: false })) : [];
        this.totals = response && response.data && response.data.data ? response.data.data.totals : {};
        this.empty = !this.items.length;
      } finally {
        this.loading = false;
      }
    },
    carrierLink(id) {
      return { name: 'CarrierDetails', params: { id } };
    },
    orderLink(id) {
      return { name: 'OrderDetails', params: { id } };
    },
    async showDetails(item) {
      this.activeCarrier = item;
      this.$bvModal.show('carrier-report-details');
      if (item.rows.length) return;
      item.loading = true;
      try {
        const response = await ApiClient.reports.carrierDetails({
          params: { ...this.form, carrierId: item.carrierId },
        });
        item.rows = response && response.data && response.data.data ? response.data.data.rows : [];
      } finally {
        item.loading = false;
      }
    },
    carrierName(item) {
      return item.carrier && item.carrier.contact ? item.carrier.contact.company : `Carrier #${item.carrierId}`;
    },
    formatCurrency(value) {
      return value === null || value === undefined ? '-' : `$${Number(value).toLocaleString()}`;
    },
    formatPercentage(value) {
      return value === null || value === undefined ? '-' : `${value}%`;
    },
    formatCountRatio(count, ratio) {
      return ratio === null || ratio === undefined ? count || 0 : `${count || 0} (${ratio}%)`;
    },
    dispatchMetrics(item) {
      return [
        { label: 'Total dispatched', value: item.totalDispatchedCount || 0 },
        { label: 'Currently assigned', value: item.currentlyDispatchedCount || 0 },
        { label: 'No longer assigned', value: this.formatCountRatio(item.undispatchedCount, item.undispatchedRatio) },
      ];
    },
    cdMetrics(item) {
      return [
        { label: 'Requests', value: item.cdRequestsCount || 0 },
        { label: 'Converted', value: item.cdConvertedRequestsCount || 0 },
        { label: 'Success rate', value: this.formatPercentage(item.cdConversionRatio) },
        { label: 'Above posted rate', value: item.cdAbovePostedRateCount || 0 },
      ];
    },
    qualityMetrics(item) {
      return [
        { label: 'Carrier mistakes', value: this.formatCountRatio(item.carrierMistakesCount, item.carrierMistakeRatio) },
        { label: 'Completed', value: item.completedCount || 0 },
        { label: 'Approvals', value: this.formatCountRatio(item.approvalsCount, item.approvalsRatio) },
      ];
    },
    pickupMetrics(item) {
      return [
        { label: 'Before window', value: item.dispatchedBeforePuw || 0 },
        { label: 'During window', value: item.dispatchedDuringPuw || 0 },
        { label: 'After window', value: item.dispatchedAfterPuw || 0 },
      ];
    },
    pickupWindowRange(item) {
      if (!item.moveDate) return 'No pickup window';
      const start = this.$options.filters.formatDate(item.moveDate);
      if (!item.moveDateLast || item.moveDateLast === item.moveDate) return start;
      return `${start} – ${this.$options.filters.formatDate(item.moveDateLast)}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.report-results ::v-deep table {
  table-layout: fixed;
}
.report-results ::v-deep thead th {
  padding: 0.75rem;
  vertical-align: middle;
}
.report-results ::v-deep tbody td,
.report-results ::v-deep tfoot th {
  padding: 0.75rem;
  vertical-align: top;
}
.report-results ::v-deep th:first-child,
.report-results ::v-deep td:first-child {
  width: 17%;
}
.report-results ::v-deep th:last-child,
.report-results ::v-deep td:last-child {
  width: 7.5rem;
  text-align: right;
}
.report-results ::v-deep th:nth-child(3),
.report-results ::v-deep td:nth-child(3) {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.report-results ::v-deep th:nth-child(n + 2),
.report-results ::v-deep td:nth-child(n + 2) {
  border-left: 1px solid rgba(0, 0, 0, 0.06);
}
.report-results ::v-deep .metric-stack {
  width: 100%;
}
::v-deep .metric-stack__item {
  display: grid;
  grid-template-columns: minmax(6.5rem, 1fr) auto;
  align-items: baseline;
  column-gap: 0.75rem;
  min-height: 1.4rem;
  line-height: 1.4;
}
::v-deep .metric-stack__label {
  color: #8b9199;
  font-size: 0.75rem;
}
::v-deep .metric-stack strong {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
::v-deep .details-table td,
::v-deep .details-table th {
  white-space: nowrap;
}
::v-deep .details-table th:nth-last-child(-n + 2),
::v-deep .details-table td:nth-last-child(-n + 2) {
  text-align: right;
  font-variant-numeric: tabular-nums;
  padding-right: 0.75rem;
}
::v-deep .details-table.table th:last-child,
::v-deep .details-table.table td:last-child {
  padding-right: 0.75rem;
}
::v-deep .details-table.table td:nth-last-child(-n + 2)::after {
  display: inline-block;
  width: 1rem;
  content: '';
}
.report-filters {
  justify-content: flex-start;
  align-items: center;
  gap: 0.75rem;
}
.report-filters .btn {
  height: 38px;
}
@media (max-width: 767.98px) {
  .report-filters .btn {
    width: 100%;
  }
}
</style>
